import { getPrototypeOf, isPrivateKey } from '@rehaje/shared'

import { Iterator } from '../constants'
import { dispatch } from '../dispatch'
import {
  ObserveClearEvent,
  ObserveDeleteEvent,
  ObserveSetEvent
} from '../event'
import { DEEP, REACTIVE } from '../flags'
import { addParent, removeParent } from '../operations/parent'
import { batch, bind, trigger } from '../operations/register'
import { createProxy } from '../proxy'
import { undispatchDepth } from '../undispatch'

import type { CollectionFunctions, Reactive } from '../types'
import type {
  TFunction,
  TIterator,
  TMap,
  TProxyHandler,
  TSet,
  TStrongStruct
} from '@rehaje/shared'

function createCallback(callback: Function): TFunction {
  return (value, key, collection) => {
    value = createProxy(value)
    if (undispatchDepth === 0) {
      addParent(collection, key, value)
    }
    callback(value, key, collection)
  }
}

function* entriesIterator(
  target: Reactive<TStrongStruct>,
  iterator: TIterator
): TIterator {
  let result = iterator.next()
  while (result.done === false) {
    if (target.__rr_flags & DEEP) {
      const key = result.value[0]
      const value = (result.value[1] = createProxy(result.value[1]))
      if (undispatchDepth === 0) {
        addParent(target, key, value)
      }
    }
    yield result.value
    result = iterator.next()
  }
}

function* valuesIterator(
  target: Reactive<TStrongStruct>,
  iterator: TIterator
): TIterator {
  let result = iterator.next()
  while (result.done === false) {
    if (target.__rr_flags & DEEP) {
      const value = (result.value = createProxy(result.value))
      if (undispatchDepth === 0) {
        addParent(target, value, value)
      }
    }
    yield result.value
    result = iterator.next()
  }
}

export const collectionFunctions: CollectionFunctions = {
  add(this: Reactive<TSet>, key: any) {
    key = key?.__rr_raw ?? key
    const raw = this.__rr_raw
    if (!raw.has(key)) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveSetEvent(raw, key, key))
          key = event.source.key
          if (raw.has(key)) return
        }
        raw.add(key)
        trigger(raw, key)
        trigger(raw, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          if (key?.__rr_flags & REACTIVE) addParent(raw, key, key)
        }
      })
    }
    return this
  },
  clear(this: Reactive<TStrongStruct>) {
    const raw = this.__rr_raw
    if (raw.size > 0) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveClearEvent(raw))
        }
        const oldTarget = raw instanceof Map ? new Map(raw) : new Set(raw)
        raw.clear()
        if (raw instanceof Map) {
          for (const [key, value] of oldTarget) {
            if (undispatchDepth === 0) {
              removeParent(raw, key, value)
            }
            trigger(raw, key)
          }
        } else {
          for (const key of oldTarget) {
            if (undispatchDepth === 0 && key?.__rr_flags & REACTIVE)
              removeParent(raw, key, key)
            trigger(raw, key)
          }
        }
        trigger(raw, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
        }
      })
    }
  },
  delete(this: Reactive<TStrongStruct>, key: any) {
    key = key?.__rr_raw ?? key
    const raw = this.__rr_raw
    if (raw.has(key)) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveDeleteEvent(raw, key))
          key = event.source.key
          if (!raw.has(key)) return
        }
        const oldVal = raw instanceof Map ? raw.get(key) : key
        raw.delete(key)
        trigger(raw, key)
        trigger(raw, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          removeParent(raw, key, oldVal)
        }
      })
    }
    return true
  },
  entries(this: Reactive<TStrongStruct>) {
    const raw = this.__rr_raw
    bind(raw, Iterator)
    const iterator = getPrototypeOf(raw).entries.call(raw)
    return entriesIterator(raw, iterator)
  },
  forEach(this: Reactive<TStrongStruct>, callback: Function, thisArg: any) {
    const raw = this.__rr_raw
    bind(raw, Iterator)
    callback = raw.__rr_flags & DEEP ? createCallback(callback) : callback
    return getPrototypeOf(raw).forEach.call(raw, callback, thisArg)
  },
  get(this: Reactive<TStrongStruct>, key: any) {
    key = key?.__rr_raw ?? key
    const raw = this.__rr_raw
    const value = raw.get(key)
    bind(raw, key)
    if (undispatchDepth === 0) {
      addParent(raw, key, value)
    }
    return raw.__rr_flags & DEEP ? createProxy(value) : value
  },
  has(this: Reactive<TStrongStruct>, key: any) {
    key = key?.__rr_raw ?? key
    const raw = this.__rr_raw
    bind(raw, key)
    return raw.has(key)
  },
  keys(this: Reactive<TStrongStruct>) {
    const raw = this.__rr_raw
    bind(raw, Iterator)
    const iterator = getPrototypeOf(raw).keys.call(raw)
    return valuesIterator(raw, iterator)
  },
  set(this: Reactive<TMap>, key: any, value: any) {
    key = key?.__rr_raw ?? key
    value = value?.__rr_raw ?? value
    const raw = this.__rr_raw
    if (value !== raw.get(key)) {
      batch(() => {
        let event
        if (undispatchDepth === 0) {
          event = dispatch(new ObserveSetEvent(raw, key, value))
          key = event.source.key
          value = event.source.value
        }
        const oldVal = raw.get(key)
        if (value === oldVal) return
        raw.set(key, value)
        trigger(raw, key)
        trigger(raw, Iterator)
        if (undispatchDepth === 0) {
          event?.__r_runEffects()
          addParent(raw, key, value)
          removeParent(raw, key, oldVal)
        }
      })
    }
    return this
  },
  get size() {
    bind(this, Iterator)
    return Reflect.get(getPrototypeOf(this), 'size', this)
  },
  [Symbol.iterator](this: Reactive<TStrongStruct>) {
    const raw = this.__rr_raw
    bind(raw, Iterator)
    const iterator = getPrototypeOf(raw)[Symbol.iterator].call(raw)
    return raw instanceof Map
      ? entriesIterator(raw, iterator)
      : valuesIterator(raw, iterator)
  },
  get [Symbol.toStringTag]() {
    return Reflect.get(getPrototypeOf(this), Symbol.toStringTag, this)
  },
  values(this: Reactive<TStrongStruct>) {
    const raw = this.__rr_raw
    bind(raw, Iterator)
    const iterator = getPrototypeOf(raw).values.call(raw)
    return valuesIterator(raw, iterator)
  }
}

export const collectionHandlers: TProxyHandler<Reactive<TStrongStruct>> = {
  get(raw, key) {
    if (isPrivateKey(key)) return raw[key]
    if (key in collectionFunctions)
      return Reflect.get(collectionFunctions, key, raw)
    return raw[key]
  }
}
